import { DateTime } from "luxon";

import type { AvailabilityAppointmentSlot } from "@/shared/agent-api/responses";
import { isStatOrUrgentPriority, SchedulingPriority } from "@/shared/models/referral";
import {
  ScheduleAvailability,
  TimeOptionsIntent,
  TimeOptionsSchema,
  TimePreference,
} from "@/shared/models/scheduling-availability";
import {
  getPriorityLookupWindow,
  getRoutineLookupWindow,
} from "@/shared/scheduling/appointment-availability-window-config";
import { adjustLeapYearDate, formatDateTimeDisplay, getDurationMinutes } from "@/shared/utils/date-utils";

export const MAX_APPOINTMENT_OPTIONS = 10;

export const MIN_APPOINTMENT_LOOKAHEAD_HOURS = 48;

export type AppointmentSlotSemanticIdentity = Readonly<{
  appointmentType: string;
  ehrAppointmentTypeId?: string | null;
  start: string;
  end: string;
  practitionerId: string | null;
  locationId: string | null;
}>;

function canonicalizeSlotDateTime(value: string): string {
  const dateTime = DateTime.fromISO(value, { setZone: true, zone: "UTC" });

  return dateTime.isValid ? dateTime.toUTC().toISO()! : value;
}

/**
 * Identifies the actual calendar opening independently of its ephemeral lookup id.
 *
 * Booking still uses the exact appointment id and signed capability returned by the
 * availability API. This identity is only for remembering an opening that the EHR has
 * already rejected so a refreshed lookup cannot re-offer it under a regenerated id.
 */
export function appointmentSlotSemanticKey(slot: AppointmentSlotSemanticIdentity): string {
  return JSON.stringify([
    slot.appointmentType,
    slot.ehrAppointmentTypeId ?? null,
    canonicalizeSlotDateTime(slot.start),
    canonicalizeSlotDateTime(slot.end),
    slot.practitionerId,
    slot.locationId,
  ]);
}

export function excludeUnavailableAppointmentSlots<T extends AppointmentSlotSemanticIdentity>(
  slots: readonly T[],
  unavailableSlotKeys: readonly string[]
): T[] {
  if (unavailableSlotKeys.length === 0) return [...slots];
  const unavailable = new Set(unavailableSlotKeys);

  return slots.filter((slot) => !unavailable.has(appointmentSlotSemanticKey(slot)));
}

export function getMinimumAppointmentStartDate(now: Date = new Date(), timezone?: string | null): string {
  const zone = timezone ?? "UTC";

  return DateTime.fromJSDate(now, { zone }).plus({ hours: MIN_APPOINTMENT_LOOKAHEAD_HOURS }).toISODate()!;
}

export function resolveScheduleAvailabilityWindow(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  now: Date = new Date(),
  timezone?: string | null,
  timePreference?: TimePreference[] | null
): ScheduleAvailability[number] {
  const adjustedStartDate = startDate ? adjustLeapYearDate(startDate, "start") : null;
  const adjustedEndDate = endDate ? adjustLeapYearDate(endDate, "end") : null;

  const minimumStartDate = getMinimumAppointmentStartDate(now);
  const resolvedStartDate =
    adjustedStartDate && adjustedStartDate > minimumStartDate ? adjustedStartDate : minimumStartDate;
  const resolvedEndDate = adjustedEndDate && adjustedEndDate >= resolvedStartDate ? adjustedEndDate : null;

  // Only attach a preference when the caller actually stated one; an empty/absent array leaves the
  // search unfiltered (matchesTimePreference treats null/empty as "all times"), same as before.
  const preference = timePreference && timePreference.length > 0 ? { timePreference } : {};

  return resolvedEndDate
    ? { intent: TimeOptionsIntent.DATE_RANGE, startDate: resolvedStartDate, endDate: resolvedEndDate, ...preference }
    : { intent: TimeOptionsIntent.AFTER_DATE, startDate: resolvedStartDate, ...preference };
}

export function resolveScheduleAvailabilityWindowByPriority(
  referralSchedulingPriority: SchedulingPriority | null,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  now: Date = new Date(),
  groupId: string,
  timezone?: string | null
): ScheduleAvailability[number] {
  const { startOffsetDays: priorityStartOffsetDays, maxWindowDays: priorityLookupMaxWindowDays } =
    getPriorityLookupWindow(groupId);
  const { startOffsetDays: routineStartOffsetDays, maxWindowDays: routineLookupMaxWindowDays } =
    getRoutineLookupWindow(groupId);
  const zone = timezone ?? "UTC";
  const today = DateTime.fromJSDate(now, { zone }).startOf("day");

  if (isStatOrUrgentPriority(referralSchedulingPriority)) {
    const boundedStartDate = today.plus({ days: priorityStartOffsetDays }).toISODate()!;
    const boundedEndDate = today.plus({ days: priorityLookupMaxWindowDays }).toISODate()!;

    return {
      intent: TimeOptionsIntent.DATE_RANGE,
      startDate: boundedStartDate,
      endDate: boundedEndDate,
    };
  }

  if (referralSchedulingPriority === SchedulingPriority.ROUTINE) {
    const routineStartDate = today.plus({ days: routineStartOffsetDays }).toISODate()!;
    const boundedStartDate = startDate && startDate >= routineStartDate ? startDate : routineStartDate;
    const maxEndDate = today.plus({ days: routineLookupMaxWindowDays }).toISODate()!;
    const boundedEndDate = endDate && endDate <= maxEndDate ? endDate : maxEndDate;
    const effectiveStartDate = boundedStartDate > boundedEndDate ? routineStartDate : boundedStartDate;

    return resolveScheduleAvailabilityWindow(effectiveStartDate, boundedEndDate, now);
  }

  return resolveScheduleAvailabilityWindow(startDate, endDate, now);
}

function formatSearchDate(date: string | Date): string | null {
  const dt = date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(date);

  return dt.isValid ? dt.toFormat("EEEE, MMMM d, yyyy") : null;
}

export function formatSearchedDateRange(startDate?: string | Date | null, endDate?: string | Date | null): string {
  const start = startDate ? formatSearchDate(startDate) : null;
  const end = endDate ? formatSearchDate(endDate) : null;

  if (start && end) return `${start} through ${end}`;
  if (start) return `on or after ${start}`;
  if (end) return `on or before ${end}`;

  return "all dates";
}

export function formatSearchedScheduleAvailability(entry: TimeOptionsSchema[number]): string {
  switch (entry.intent) {
    case TimeOptionsIntent.SPECIFIC_DATE: {
      const date = formatSearchDate(entry.date);

      return date ? `on ${date}` : "all dates";
    }

    case TimeOptionsIntent.DATE_RANGE:
      return formatSearchedDateRange(entry.startDate, entry.endDate);

    case TimeOptionsIntent.AFTER_DATE:
      return formatSearchedDateRange(entry.startDate);

    case TimeOptionsIntent.WEEK_OF: {
      const weekOfDate = formatSearchDate(entry.weekOfDate);

      return weekOfDate ? `week of ${weekOfDate}` : "all dates";
    }

    default:
      return "all dates";
  }
}

export function formatAvailabilitySlotsForAgent(slots: AvailabilityAppointmentSlot[]): (Omit<
  AvailabilityAppointmentSlot,
  "appointmentSlotActionId"
> & {
  display: string;
  durationMinutes: number;
})[] {
  const byStart = (a: AvailabilityAppointmentSlot, b: AvailabilityAppointmentSlot) =>
    Date.parse(a.start) - Date.parse(b.start);

  const slotsByProvider = new Map<string, AvailabilityAppointmentSlot[]>();

  for (const slot of slots) {
    if (!slot.practitionerId) continue;
    const key = slot.practitionerId;
    const existing = slotsByProvider.get(key);

    if (existing) {
      existing.push(slot);
    } else {
      slotsByProvider.set(key, [slot]);
    }
  }

  return [...slotsByProvider.values()]
    .flatMap((providerSlots) => providerSlots.sort(byStart).slice(0, MAX_APPOINTMENT_OPTIONS))
    .sort(byStart)
    .map((slot) => {
      const start = new Date(slot.start);
      const end = new Date(slot.end);
      const { appointmentSlotActionId: _appointmentSlotActionId, ...modelVisibleSlot } = slot;

      return {
        ...modelVisibleSlot,
        display: formatDateTimeDisplay(slot.start),
        durationMinutes: getDurationMinutes(start, end),
      };
    });
}
